import { useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { useColorMode } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { useSuccessPhotoCardToast } from '../../presentation/template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { useUpdateSatisfaction } from '@pocapan/shared/domains/chat/ui/hooks/useUpdateSatisfaction';
import { attachJosa } from '@pocapan/shared/utils/bettieUtils';
import PulsingGradientCircle from '../../presentation/chat/components/PulsingGradientCircle';
import { HorizontalSpeechBallon } from './BettieNicknameUpdateBottomDrawer';
import ProfileAvatar from '../../presentation/my-page/components/ProfileAvatar';

const SATISFACTION_OPTIONS = [
  {
    label: 'BettieSatisfaction.good',
    emoji: 'Smiling%20face%20with%20hearts/3D/smiling_face_with_hearts_3d',
    color: '#FF45A9',
    dropShadowLight: 'rgba(255, 69, 169, 0.4)',
    dropShadowDark: 'rgba(255, 69, 169, 0.8)',
    value: 'satisfied'
  },
  {
    label: 'BettieSatisfaction.soSo',
    emoji: 'Thinking%20face/3D/thinking_face_3d',
    color: '#FF8000',
    dropShadowLight: 'rgba(255, 128, 0, 0.5)',
    dropShadowDark: 'rgba(255, 128, 0, 0.8)',
    value: 'fair'
  },
  {
    label: 'BettieSatisfaction.bad',
    emoji: 'Pensive%20face/3D/pensive_face_3d',
    color: '#FF142A',
    dropShadowLight: 'rgba(255, 20, 42, 0.4)',
    dropShadowDark: 'rgba(255, 20, 42, 0.8)',
    value: 'poor'
  }
];

interface BettieSatisfactionBottomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  thumbnailUrl: string;
  nickname: string;
  chatRoomId: string;
}

const BettieSatisfactionBottomDrawer = ({
  isOpen,
  onClose,
  thumbnailUrl,
  nickname,
  chatRoomId
}: BettieSatisfactionBottomDrawerProps) => {
  const { t } = useTranslation();
  const showSuccessToast = useSuccessPhotoCardToast();
  const { mutateAsync: updateSatisfaction } = useUpdateSatisfaction();

  const [selectedSatisfaction, setSelectedSatisfaction] = useState<
    'satisfied' | 'fair' | 'poor' | null
  >(null);
  const [comment, setComment] = useState<string>('');
  const handleSelectSatisfaction = (value: 'satisfied' | 'fair' | 'poor') => {
    if (value === 'satisfied') {
      setComment('');
    }
    setSelectedSatisfaction(value);
  };

  const handleConfirm = async () => {
    if (!selectedSatisfaction) return;
    try {
      const requestBody = {
        chat_room_feedback: {
          rating: selectedSatisfaction,
          comment: comment
        }
      };
      const response = await updateSatisfaction({
        body: requestBody,
        chatRoomId
      });
      if (response?.chatRoomFeedbackId) {
        onClose();
        showSuccessToast('bottom', t('BettieSatisfaction.toast') as string);
      }
    } catch (error) {
      console.error(error);
    } finally {
      onClose();
    }
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content">
      <Sheet.Container className="relative !rounded-t-[20px] !bg-background-light dark:!bg-background-elevated">
        <div className="px-[24px] py-[40px] flex flex-col gap-[36px] items-center">
          <div className="flex items-center gap-[27px]">
            <PulsingGradientCircle
              gradientCircleSize="50px"
              glassCircleSize="50px"
              showDottedCircle={false}
              blur="4.7px"
            >
              <ProfileAvatar
                thumbnailUrl={thumbnailUrl}
                widthAndHeight="45px"
                svgWidthAndHeight="45px"
              />
            </PulsingGradientCircle>
            <HorizontalSpeechBallon
              text={t('BettieSatisfaction.question', { nickname: attachJosa(nickname, '아/야') })}
            />
          </div>
          <div className="flex items-center gap-[8px] justify-center">
            {SATISFACTION_OPTIONS.map((option) => (
              <SatisfactionOptionButton
                key={option.value}
                option={option}
                isSelected={selectedSatisfaction === option.value}
                onClick={() =>
                  handleSelectSatisfaction(option.value as 'satisfied' | 'fair' | 'poor')
                }
              />
            ))}
          </div>
          {(selectedSatisfaction === 'fair' || selectedSatisfaction === 'poor') && (
            <div className="p-[20px] bg-secondary-light dark:bg-secondary-dark rounded-[12px] w-full h-[80px]">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={String(t('BettieSatisfaction.placeholder'))}
                className="w-full h-full resize-none outline-none bg-transparent text-[17px] font-semibold leading-[20px]"
              />
            </div>
          )}
          <p className="text-[15px] font-medium text-text-main-lv3 leading-[18px]">
            {t('BettieSatisfaction.description')}
          </p>
        </div>
        <Button
          size="fill"
          className="text-[17px] font-semibold leading-[20px]"
          disabled={!selectedSatisfaction}
          onClick={handleConfirm}
        >
          {t('done')}
        </Button>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-[rgba(0,0,0,0.50)]" />
    </Sheet>
  );
};

export default BettieSatisfactionBottomDrawer;

const SatisfactionOptionButton = ({
  option,
  isSelected,
  onClick
}: {
  option: (typeof SATISFACTION_OPTIONS)[0];
  isSelected: boolean;
  onClick: () => void;
}) => {
  const { colorMode } = useColorMode();
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className="px-[20px] py-[14px] flex flex-col gap-[10px] items-center"
    >
      <img
        src={`https://cdn.jsdelivr.net/gh/microsoft/fluentui-emoji@main/assets/${option.emoji}.png`}
        alt="Fluent Emoji"
        width="48"
        style={{
          filter: isSelected
            ? `drop-shadow(0px 0px 5px ${
                colorMode === 'dark' ? option.dropShadowDark : option.dropShadowLight
              })`
            : 'none'
        }}
      />
      <div className="relative">
        <p
          className="text-[15px] font-medium leading-[18px]"
          style={{
            color: isSelected ? option.color : 'var(--text-main-lv3)'
          }}
        >
          {t(option.label)}
        </p>
        {isSelected && <BlurBackground color={option.color} />}
      </div>
    </button>
  );
};

const BlurBackground = ({ color }: { color: string }) => {
  const { colorMode } = useColorMode();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="76"
      height="35"
      viewBox="0 0 76 35"
      fill="none"
      aria-label="Selected Satisfaction"
      role="img"
      className="absolute top-1 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full"
    >
      <g filter="url(#filter0_f_9153_33703)">
        <ellipse cx="37.6001" cy="17.1001" rx="26.5" ry="6" fill="url(#paint0_radial_9153_33703)" />
      </g>
      <defs>
        <filter
          id="filter0_f_9153_33703"
          x="9.72748e-05"
          y="9.72748e-05"
          width="75.2"
          height="34.2"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="5.55" result="effect1_foregroundBlur_9153_33703" />
        </filter>
        <radialGradient
          id="paint0_radial_9153_33703"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(37.6001 17.1001) scale(26.5 6)"
        >
          <stop stopColor={color} stopOpacity={colorMode === 'dark' ? 0.4 : 0.25} />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
};
